import { useCallback, useEffect, useState } from 'react'
import { getPublicSchedule } from '../../shared/api/public.js'
import type { PublicSchedule } from '../../shared/api/public-types.js'

export type ClosedNoticePhase = 'none' | 'dialog' | 'bar'

const FOCUS_REFRESH_THROTTLE_MS = 30_000
const MAX_TIMEOUT_MS = 2_147_000_000

export function useStorefrontSchedule(orgSlug: string, locationSlug: string | null) {
  const [schedule, setSchedule] = useState<PublicSchedule | null>(null)
  const [noticePhase, setNoticePhase] = useState<ClosedNoticePhase>('none')
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    setSchedule(null)
    setNoticePhase('none')
  }, [orgSlug, locationSlug])

  useEffect(() => {
    if (!orgSlug || !locationSlug) return
    let cancelled = false

    getPublicSchedule(orgSlug, locationSlug)
      .then(next => {
        if (!cancelled) setSchedule(next)
      })
      .catch(() => {
        // Keep the last known state. Checkout is still protected server-side.
      })

    return () => { cancelled = true }
  }, [orgSlug, locationSlug, refreshToken])

  useEffect(() => {
    if (!schedule) return
    if (schedule.acceptingOrders) {
      setNoticePhase('none')
      return
    }
    // Show the dialog on the initial closed state (and on a new closed period).
    // Acknowledging it or a later refresh must not bring it back.
    setNoticePhase(previous => previous === 'none' ? 'dialog' : previous)
  }, [schedule])

  // Wake up exactly when the next opening is expected, then re-check the
  // server state. No periodic polling while the location is closed.
  useEffect(() => {
    if (!schedule || schedule.acceptingOrders || !schedule.nextOpenTime) return

    const target = new Date(schedule.nextOpenTime).getTime()
    if (!Number.isFinite(target)) return

    const msUntilOpen = target - Date.now()
    // If the advertised opening is already in the past (clock skew), do not
    // hammer the API every second. Retry with a calm backoff instead.
    const delay = msUntilOpen > 0
      ? Math.min(msUntilOpen + 1_000, MAX_TIMEOUT_MS)
      : 60_000
    const timer = window.setTimeout(() => setRefreshToken(value => value + 1), delay)
    return () => window.clearTimeout(timer)
  }, [schedule])

  // Catch up after a background tab, device sleep or network reconnect.
  useEffect(() => {
    if (!orgSlug || !locationSlug) return
    let lastRefreshAt = Date.now()

    const refreshIfVisible = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastRefreshAt < FOCUS_REFRESH_THROTTLE_MS) return
      lastRefreshAt = now
      setRefreshToken(value => value + 1)
    }

    window.addEventListener('focus', refreshIfVisible)
    document.addEventListener('visibilitychange', refreshIfVisible)
    return () => {
      window.removeEventListener('focus', refreshIfVisible)
      document.removeEventListener('visibilitychange', refreshIfVisible)
    }
  }, [orgSlug, locationSlug])

  const refresh = useCallback(async (): Promise<PublicSchedule | null> => {
    if (!orgSlug || !locationSlug) return null
    try {
      const next = await getPublicSchedule(orgSlug, locationSlug)
      setSchedule(next)
      return next
    } catch {
      return null
    }
  }, [orgSlug, locationSlug])

  const acknowledgeNotice = useCallback(() => setNoticePhase('bar'), [])

  return {
    schedule,
    noticePhase,
    orderingBlocked: schedule?.acceptingOrders === false,
    acknowledgeNotice,
    refresh,
  }
}
